import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Switch,
  Input,
  Button,
  Spinner,
  Text,
  Body1,
  Caption1,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  AddRegular,
  DismissRegular,
  SearchRegular,
  GlobeRegular,
  ShieldKeyholeRegular,
} from '@fluentui/react-icons';
import { useGraphConsent } from '../../hooks/useGraphConsent';
import { useSharePointConsent, type ConsentOutcome } from '../../hooks/useSharePointConsent';
import {
  getRecentSites,
  searchSites,
  SitesConsentRequiredError,
} from '../../services/discoveryApi';
import type { SiteSummary } from '../../types/discovery.types';

const MAX_SITES = 10;

interface SitePickerProps {
  selectedSites: SiteSummary[];
  onChange: (sites: SiteSummary[]) => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  selectedList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '220px',
    overflowY: 'auto',
  },
  siteRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  siteInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  siteName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  adminActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalS,
  },
});

/**
 * Opt-in SharePoint site-list builder (Mode B).
 *
 * Off by default. When enabled it requests Sites.Read.All; admins consent in one
 * click, non-admins are routed to the admin-approval flow. The user searches and
 * adds up to 10 sites to crawl. Selected sites are owned by the parent form.
 */
export function SitePicker({ selectedSites, onChange }: SitePickerProps) {
  const styles = useStyles();
  const { acquireApiToken } = useGraphConsent();
  const { ensureConsent, triggerAdminConsent, adminConsentUrl, isConsenting } =
    useSharePointConsent();

  const [enabled, setEnabled] = useState(false);
  const [consent, setConsent] = useState<ConsentOutcome | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [copied, setCopied] = useState(false);

  const consentOk = consent === 'ok';

  // Debounce the search term
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const recentQuery = useQuery({
    queryKey: ['sites', 'recent'],
    queryFn: async () => {
      const token = await acquireApiToken();
      if (!token) throw new Error('Could not acquire access token.');
      return getRecentSites(token);
    },
    enabled: consentOk,
    retry: false,
  });

  const searchQuery = useQuery({
    queryKey: ['sites', 'search', debounced],
    queryFn: async () => {
      const token = await acquireApiToken();
      if (!token) throw new Error('Could not acquire access token.');
      return searchSites(debounced, token);
    },
    enabled: consentOk && debounced.length >= 2,
    retry: false,
  });

  // If a fetch reveals consent is actually missing, route to the admin flow.
  useEffect(() => {
    const err = recentQuery.error ?? searchQuery.error;
    if (err instanceof SitesConsentRequiredError) {
      setConsent('admin_required');
    }
  }, [recentQuery.error, searchQuery.error]);

  const handleToggle = async (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange([]);
      setConsent(null);
      return;
    }
    if (consent !== 'ok') {
      const outcome = await ensureConsent();
      setConsent(outcome);
    }
  };

  const selectedIds = useMemo(
    () => new Set(selectedSites.map((s) => s.id)),
    [selectedSites]
  );
  const atLimit = selectedSites.length >= MAX_SITES;

  const addSite = (site: SiteSummary) => {
    if (atLimit || selectedIds.has(site.id)) return;
    onChange([...selectedSites, site]);
  };
  const removeSite = (id: string) => {
    onChange(selectedSites.filter((s) => s.id !== id));
  };

  const copyAdminUrl = async () => {
    try {
      await navigator.clipboard.writeText(adminConsentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard unavailable — the link is still shown */
    }
  };

  const candidates: SiteSummary[] =
    debounced.length >= 2 ? searchQuery.data ?? [] : recentQuery.data ?? [];
  const isLoadingList =
    debounced.length >= 2 ? searchQuery.isLoading : recentQuery.isLoading;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <GlobeRegular fontSize={20} color={tokens.colorBrandForeground1} />
        <div className={styles.headerText}>
          <Switch
            checked={enabled}
            disabled={isConsenting}
            onChange={(_, data) => handleToggle(data.checked)}
            label="Include specific SharePoint sites for deeper context (optional)"
          />
          <Caption1>
            Off by default. When on, we read only the sites you pick below (up to{' '}
            {MAX_SITES}) to tailor your report. Requires your IT admin to approve
            read-only SharePoint access.
          </Caption1>
        </div>
      </div>

      {enabled && (
        <div className={styles.body}>
          {isConsenting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              <Caption1>Requesting SharePoint access…</Caption1>
            </div>
          )}

          {consent === 'admin_required' && (
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Your IT admin needs to approve this</MessageBarTitle>
                Reading SharePoint sites needs a one-time admin approval for
                read-only access. Ask an administrator to approve it, or send them
                the link below. You can still generate your report without this.
                <div className={styles.adminActions}>
                  <Button
                    appearance="primary"
                    icon={<ShieldKeyholeRegular />}
                    onClick={triggerAdminConsent}
                  >
                    Request admin approval
                  </Button>
                  <Button appearance="secondary" onClick={copyAdminUrl}>
                    {copied ? 'Link copied' : 'Copy link for IT'}
                  </Button>
                </div>
              </MessageBarBody>
            </MessageBar>
          )}

          {consent === 'error' && (
            <MessageBar intent="error">
              <MessageBarBody>
                We couldn't set up SharePoint access. You can still generate your
                report from the details above, or try again.
              </MessageBarBody>
            </MessageBar>
          )}

          {consentOk && (
            <>
              {selectedSites.length > 0 && (
                <div className={styles.selectedList}>
                  {selectedSites.map((s) => (
                    <span key={s.id} className={styles.chip}>
                      <Caption1>{s.name}</Caption1>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        aria-label={`Remove ${s.name}`}
                        onClick={() => removeSite(s.id)}
                      />
                    </span>
                  ))}
                </div>
              )}

              <Caption1>
                {selectedSites.length}/{MAX_SITES} sites selected
                {atLimit ? ' — remove one to add another' : ''}
              </Caption1>

              <Input
                contentBefore={<SearchRegular />}
                placeholder="Search your SharePoint sites…"
                value={searchTerm}
                onChange={(_, data) => setSearchTerm(data.value)}
              />

              <div>
                <Caption1>
                  {debounced.length >= 2 ? 'Search results' : 'Recently used sites'}
                </Caption1>
                {isLoadingList ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Spinner size="tiny" />
                    <Caption1>Loading sites…</Caption1>
                  </div>
                ) : candidates.length === 0 ? (
                  <Caption1>
                    {debounced.length >= 2
                      ? 'No sites matched your search.'
                      : 'No recent sites found — use the search box above.'}
                  </Caption1>
                ) : (
                  <div className={styles.resultsList}>
                    {candidates.map((site) => {
                      const added = selectedIds.has(site.id);
                      return (
                        <div key={site.id} className={styles.siteRow}>
                          <div className={styles.siteInfo}>
                            <Body1 className={styles.siteName}>{site.name}</Body1>
                            {site.url && (
                              <Caption1 className={styles.siteName} style={{ color: tokens.colorNeutralForeground3 }}>
                                {site.url}
                              </Caption1>
                            )}
                          </div>
                          <Button
                            appearance={added ? 'subtle' : 'secondary'}
                            size="small"
                            icon={<AddRegular />}
                            disabled={added || atLimit}
                            onClick={() => addSite(site)}
                          >
                            {added ? 'Added' : 'Add'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                We read only the sites you add here. Your report will include an
                audit trail of exactly what was accessed.
              </Text>
            </>
          )}
        </div>
      )}
    </div>
  );
}
